// The pending kiosk/app orders queue at the top of Sell (F-POS-10, spec/13 §4).
//
// A player who chose "Pay at the counter" on the kiosk or in the app leaves an order that is placed and has taken no
// payment. It lands here — kept live over `venue:{v}:orders` (`../orders/realtime.ts`), so it appears within the
// channel's latency without a refresh — with **Take payment**, which opens it in the counter checkout, and **Reject**,
// which cancels it (allowed only before a payment settled, `orders:void`) and releases any code use it reserved.
// Staff hear of a new one through N-19 on the floor alert channel. The queue hides itself when it is empty.
//
// "Scan & pay (UPI)" (F-PAY-02, 2026-10-01): an order the player paid by scanning the venue's own UPI QR at the PC carries
// a "UPI scan" badge and its reference (the payment's UPI note) — staff match it in their UPI app and Take payment opens
// with UPI chosen, the ordinary counter-UPI confirmation.
//
// A **console session** (ADR-0034): the one order a staff-run session placed when it ended — its booked blocks and
// controllers — waits here with a "Console session" badge until it is paid at the counter, like any order.

import type { Sale } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { problemCode, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { useAwaitingPayment, useRejectOrder } from '../orders/queries';
import { useOrdersLive } from '../orders/realtime';
import { useStations } from '../../stations/queries';

export function AwaitingPaymentQueue({
  organizationId,
  venueId,
  canTake,
  canReject,
  onTake,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  /** `orders:create`: take a tender against the order. */
  readonly canTake: boolean;
  /** `orders:void`: cancel it. */
  readonly canReject: boolean;
  readonly onTake: (sale: Sale) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const { show } = useToast();
  const live = useOrdersLive(organizationId, venueId, true);
  const queue = useAwaitingPayment(organizationId, venueId, true, live);
  const stations = useStations(organizationId, venueId);
  const reject = useRejectOrder(organizationId, venueId);
  const [confirming, setConfirming] = useState<string | null>(null);

  const orders = queue.data ?? [];
  if (orders.length === 0) return null;

  const stationLabel = (stationId: string | null): string => {
    if (stationId === null) return t('sell.awaiting.app');
    const station = (stations.data?.pages ?? []).flatMap((page) => page.data).find((candidate) => candidate.id === stationId);
    if (station === undefined) return t('sell.awaiting.kiosk');
    return station.name ?? t('sell.awaiting.station', { number: station.number });
  };

  function doReject(sale: Sale): void {
    reject.mutate(sale.id, {
      onSuccess: () => {
        setConfirming(null);
        show({ tone: 'ok', title: t('sell.awaiting.rejected') });
      },
      onError: (error) => {
        setConfirming(null);
        show({ tone: 'danger', title: problemCode(error) === PROBLEM.conflict ? t('sell.awaiting.rejectPaid') : problem.text(error, { fallback: t('sell.awaiting.rejectFailed') }) });
      },
    });
  }

  return (
    <section className="console-sell-queue" aria-labelledby="sell-queue-title" aria-live="polite">
      <h2 className="console-sell-queue__title" id="sell-queue-title">
        {t('sell.awaiting.title')}
        <span className="console-sell-group__count console-num">{orders.length}</span>
      </h2>
      <ul className="console-sell-queue__list">
        {orders.map((sale) => (
          <li key={sale.id} className="console-sell-queue__item">
            <span className="console-sell-queue__what">
              <span className="console-sell-queue__name">
                {sale.orderNumber === null || sale.orderNumber === undefined ? t('sell.awaiting.order') : t('sell.awaiting.numbered', { number: String(sale.orderNumber).padStart(4, '0') })}
                <span className="console-subtle"> · {stationLabel(sale.stationId)}</span>
                {sale.origin === 'console_session' ? (
                  <span className="console-sell-queue__scan">
                    {' '}
                    <Badge tone="info">{t('sell.awaiting.consoleSession')}</Badge>
                  </span>
                ) : null}
                {sale.scanToPayReference !== undefined ? (
                  // "Scan & pay (UPI)" (F-PAY-02): paid by the venue's own QR at the PC; the reference is the payment's note.
                  <span className="console-sell-queue__scan" title={t('sell.awaiting.scanHint')}>
                    {' '}
                    <Badge tone="accent">{t('sell.awaiting.scanBadge')}</Badge>{' '}
                    <span className="console-num">{t('sell.awaiting.scanReference', { reference: sale.scanToPayReference })}</span>
                  </span>
                ) : null}
              </span>
              <span className="console-subtle">{sale.lines.map((line) => (line.quantity > 1 ? `${line.quantity} × ${line.name}` : line.name)).join(', ')}</span>
            </span>
            <strong className="console-sell-queue__total console-num">{format.money(sale.total)}</strong>
            <span className="console-sell-queue__actions">
              {canTake ? (
                <Button variant="primary" size="sm" onClick={() => onTake(sale)}>
                  {t('sell.awaiting.take')}
                </Button>
              ) : null}
              {canReject ? (
                confirming === sale.id ? (
                  <Button variant="danger" size="sm" busy={reject.isPending} onClick={() => doReject(sale)}>
                    {t('sell.awaiting.confirmReject')}
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => setConfirming(sale.id)}>
                    {t('sell.awaiting.reject')}
                  </Button>
                )
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
